import React, { useMemo, useState } from "react";
import { Check, X, ArrowUpRight } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import FormConsent from "../components/site/FormConsent";
import {
  FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import { getCtaVariant } from "../lib/ab";

const QUESTIONS = [
  { id: "gbp", area: "Google", q: "Is your Google Business Profile fully filled out (cover, interior, exterior, dishes)?", weight: 14 },
  { id: "photos", area: "Photography", q: "Do you have professional photos of every dish (not phone snaps)?", weight: 16 },
  { id: "website", area: "Website", q: "Does your website load in under 3 seconds on mobile and clearly show how to book?", weight: 12 },
  { id: "social", area: "Social", q: "Do you post original photo/video content at least twice a week?", weight: 12 },
  { id: "delivery", area: "Delivery", q: "Is your Uber Eats / DoorDash menu fully photographed and consistently lit?", weight: 14 },
  { id: "reviews", area: "Reviews", q: "Do you have a system that asks satisfied diners to leave a Google review?", weight: 10 },
  { id: "paid", area: "Paid ads", q: "Are you running paid ads (Meta or Google) with measurable conversions?", weight: 12 },
  { id: "email", area: "Email / CRM", q: "Do you collect customer emails and send at least one promotional message a month?", weight: 10 },
];

const TIER = (score) => {
  if (score >= 80) return { grade: "A+", title: "Top of class.", color: "text-ember", desc: "You're running marketing properly. The only thing left is to maintain quality and refresh content quarterly." };
  if (score >= 65) return { grade: "A", title: "Strong.", color: "text-ember", desc: "Above-average across the board. One or two areas (usually paid ads + email) are where you'd extract the most extra growth." };
  if (score >= 50) return { grade: "B", title: "Functional but leaking.", color: "text-bone", desc: "You're doing the basics, but missing two or three high-leverage moves. Photography and Google are usually the highest-ROI fixes." };
  if (score >= 30) return { grade: "C", title: "Significant gaps.", color: "text-bone/85", desc: "Several core marketing channels are underperforming. Most venues in this band see 20–40% growth within 90 days of a focused fix-up." };
  return { grade: "D", title: "Almost everything is broken.", color: "text-ember", desc: "Your marketing is leaving most of its potential on the table. A 30-minute strategy call is the cheapest way to map the next 6 months." };
};

export default function RestaurantMarketingScore() {
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState(false);
  const [lead, setLead] = useState({ name: "", restaurant: "", email: "", phone: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const score = useMemo(() => QUESTIONS.reduce((acc, q) => acc + (answers[q.id] === "yes" ? q.weight : 0), 0), [answers]);
  const answered = QUESTIONS.every((q) => answers[q.id]);
  const tier = TIER(score);

  const setAns = (id, v) => setAnswers((s) => ({ ...s, [id]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const variant = getCtaVariant();
      const answerSummary = QUESTIONS.map((q) => `${q.area}: ${q.q} → ${answers[q.id] || "—"}`).join("\n");
      await fetch(`${API}/api/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...lead,
          source: "restaurant-marketing-score",
          message: `Marketing score: ${score}/100 (${tier.grade})\n\n${answerSummary}`,
          cta_variant: variant.id,
        }),
      });
      setSent(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />

      <section className="relative pt-32 lg:pt-40 pb-12 lg:pb-16 overflow-hidden bg-ink">
        <div className="absolute inset-0 opacity-30"><Photo slug="hero-banquet-01" alt="" ratio="auto" className="!relative h-full w-full" /></div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/55 to-ink" />
        <div className="relative mx-auto max-w-[1100px] px-6 lg:px-10">
          <Breadcrumb current="Restaurant Marketing Score" />
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 font-bold">Free scorecard · 2 minutes · Honest result</p>
          <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
            How well is your
            <br />
            restaurant <span className="italic font-light text-ember">marketed?</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-bone/80">
            Eight honest questions across Google, photography, website, social,
            delivery apps, reviews, paid ads and email. We score you 0–100 and
            tell you the exact 2–3 things that would lift the score most.
          </p>
        </div>
      </section>

      {/* What we score */}
      <section className="bg-bone text-ink py-16 lg:py-20 border-b border-ink/10">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          <p className="text-xs uppercase tracking-wider2 text-ember mb-4 font-bold">What this scorecard measures</p>
          <h2 className="font-display tracking-editorial leading-[0.95] mb-6" style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)" }}>
            Eight visual and discovery channels — weighted by how much they actually drive trade.
          </h2>
          <p className="text-char text-base md:text-lg leading-relaxed mb-6 max-w-3xl">
            Every Melbourne restaurant&apos;s marketing breaks down into a handful of measurable signals. Get any one of them wrong and orders leak out. This scorecard checks:
          </p>
          <ul className="space-y-2.5 text-base text-char">
            <li className="flex gap-3"><span className="text-ember font-bold shrink-0">·</span> <span><strong>Google Business Profile completeness</strong> — cover photo, interior, exterior, dish photography, hours, menu link, recent updates.</span></li>
            <li className="flex gap-3"><span className="text-ember font-bold shrink-0">·</span> <span><strong>Delivery app photo quality</strong> — Uber Eats, DoorDash and Menulog thumbnails to platform spec, every dish photographed, white-balance consistent across the menu.</span></li>
            <li className="flex gap-3"><span className="text-ember font-bold shrink-0">·</span> <span><strong>Website imagery</strong> — hero shot, menu page photography, venue ambience, freshness (under 18 months old).</span></li>
            <li className="flex gap-3"><span className="text-ember font-bold shrink-0">·</span> <span><strong>Social media visual consistency</strong> — Instagram grid coherence, brand colour palette held across posts, recent activity (last 30 days).</span></li>
            <li className="flex gap-3"><span className="text-ember font-bold shrink-0">·</span> <span><strong>Reviews</strong> — Google rating, recency of newest review, response rate on negative reviews.</span></li>
            <li className="flex gap-3"><span className="text-ember font-bold shrink-0">·</span> <span><strong>Paid acquisition</strong> — whether you&apos;re actively running Google Ads or Meta Ads for suburb-level discovery.</span></li>
            <li className="flex gap-3"><span className="text-ember font-bold shrink-0">·</span> <span><strong>Email + retention</strong> — capture mechanism on your website, frequency of comms to past diners.</span></li>
          </ul>
        </div>
      </section>

      <section className="bg-bone text-ink py-20 lg:py-28">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          {!revealed && (
            <>
              <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-3">Eight questions</p>
              <h2 className="font-display tracking-editorial leading-[0.95] mb-12" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
                Be honest.<br /><span className="italic font-light text-ember">It only takes 2 minutes.</span>
              </h2>

              <div className="space-y-7">
                {QUESTIONS.map((q, i) => (
                  <div key={q.id} className="border-b border-ink/15 pb-7" data-testid={`score-q-${q.id}`}>
                    <div className="flex items-baseline gap-4 mb-3">
                      <span className="font-sans font-bold text-sm text-ember tracking-tight shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <div className="flex-1">
                        <p className="text-[10px] uppercase tracking-wider2 font-bold text-char-mute mb-1">{q.area} · {q.weight} pts</p>
                        <h3 className="font-display text-lg md:text-xl tracking-editorial leading-tight">{q.q}</h3>
                      </div>
                    </div>
                    <div className="flex gap-3 ml-9">
                      {["yes", "no"].map((v) => (
                        <button
                          key={v}
                          type="button"
                          data-testid={`score-${q.id}-${v}`}
                          onClick={() => setAns(q.id, v)}
                          className={`px-5 py-2 text-xs uppercase tracking-wider2 font-bold border transition-all ${
                            answers[q.id] === v
                              ? (v === "yes" ? "border-ember bg-ember text-bone" : "border-ink bg-ink text-bone")
                              : "border-ink/15 text-char hover:border-ink"
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-xs text-char-mute">
                  {answered ? "All questions answered." : `${Object.keys(answers).length} of ${QUESTIONS.length} answered.`}
                </p>
                <button
                  type="button"
                  disabled={!answered}
                  onClick={() => setRevealed(true)}
                  data-testid="score-reveal"
                  className="inline-flex items-center gap-2 bg-ember text-bone font-sans font-bold uppercase tracking-wider2 text-xs px-7 py-4 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-ember-deep transition-colors"
                >
                  Reveal My Score <ArrowUpRight size={14} strokeWidth={2} />
                </button>
              </div>
            </>
          )}

          {revealed && (
            <div data-testid="score-result">
              <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-3">Your marketing score</p>
              <div className="flex flex-col md:flex-row md:items-end gap-8 md:gap-12 mb-12">
                <div>
                  <p className={`font-sans font-bold tracking-tight leading-none ${tier.color}`} style={{ fontSize: "clamp(6rem, 16vw, 12rem)" }}>{score}</p>
                  <p className="text-xs uppercase tracking-wider2 text-ember font-bold mt-2">Grade: {tier.grade}</p>
                </div>
                <div className="flex-1">
                  <h2 className="font-display tracking-editorial leading-[0.95] mb-5" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
                    {tier.title}
                  </h2>
                  <p className="text-base md:text-lg text-char leading-relaxed">{tier.desc}</p>
                </div>
              </div>

              {/* Per-question breakdown */}
              <div className="border-t border-ink/15 pt-10 mb-12">
                <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-6">Where you scored</p>
                <ul className="space-y-3">
                  {QUESTIONS.map((q) => (
                    <li key={q.id} className="flex items-start gap-4 text-sm md:text-base">
                      {answers[q.id] === "yes"
                        ? <Check size={18} className="text-ember mt-0.5 shrink-0" />
                        : <X size={18} className="text-ink/40 mt-0.5 shrink-0" />}
                      <span className="flex-1">
                        <span className={answers[q.id] === "yes" ? "text-char" : "text-char/55"}>{q.q}</span>
                        <span className={`ml-2 text-xs font-bold ${answers[q.id] === "yes" ? "text-ember" : "text-char/40"}`}>
                          {answers[q.id] === "yes" ? `+${q.weight}` : "0"}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Grownomics intro */}
              <div className="bg-ink text-bone p-7 md:p-10 mb-12">
                <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-4">Built with Grownomics</p>
                <h3 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mb-5">
                  This scorecard was developed in partnership with
                  <a href="https://grownomics.com.au" target="_blank" rel="noopener noreferrer" className="italic font-light text-ember hover:underline ml-2">Grownomics</a>.
                </h3>
                <p className="text-sm md:text-base text-bone/80 leading-relaxed max-w-2xl">
                  Grownomics is a hospitality-specialist digital marketing agency
                  based in Melbourne. They run paid ads, Google, websites, email
                  and SEO for restaurants, cafés and venue groups — the same
                  channels graded above. If your score is below 65, a quick chat
                  with them is usually the most efficient next step.
                </p>
                <a href="https://grownomics.com.au" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-6 text-xs uppercase tracking-wider2 font-bold text-ember hover:text-bone transition-colors">
                  Visit Grownomics <ArrowUpRight size={12} strokeWidth={2} />
                </a>
              </div>

              {/* Photography-focused lead capture */}
              {!sent ? (
                <form onSubmit={onSubmit} className="bg-bone border border-ink/10 p-7 md:p-10">
                  <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-3">Get a personalised score report</p>
                  <h3 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mb-3">
                    Want a one-page action plan?
                  </h3>
                  <p className="text-sm md:text-base text-char-mute leading-relaxed mb-8 max-w-2xl">
                    We&apos;ll send a personalised PDF showing the 3 highest-leverage
                    fixes for your specific score, with rough timelines and indicative
                    costs. No commitment.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                    {[
                      ["name", "Your name", "text"],
                      ["restaurant", "Venue name", "text"],
                      ["email", "Email *", "email"],
                      ["phone", "Phone *", "tel"],
                    ].map(([k, label, type]) => (
                      <label key={k} className="block">
                        <span className="text-xs uppercase tracking-wider2 font-bold text-char/70 block mb-2">{label}</span>
                        <input
                          type={type}
                          required
                          data-testid={`score-lead-${k}`}
                          value={lead[k]}
                          onChange={(e) => setLead((s) => ({ ...s, [k]: e.target.value }))}
                          className="w-full bg-transparent border-b border-ink/15 focus:border-ember text-ink font-display text-xl tracking-editorial pb-2 focus:outline-none transition-colors"
                        />
                      </label>
                    ))}
                  </div>
                  <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                    <p className="text-xs text-char-mute max-w-sm leading-relaxed">No spam. One personalised email, then a phone call if you want one.</p>
                    <CTAButton type="submit" variant="primary" size="md" disabled={sending}>{sending ? "Sending…" : "Send My Action Plan"}</CTAButton>
                  </div>
                  <FormConsent className="mt-6" />
                </form>
              ) : (
                <div data-testid="score-sent" className="bg-bone border border-ember/30 p-7 md:p-10">
                  <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-3">Action plan request received</p>
                  <h3 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mb-3">
                    Thanks — we&apos;ll send your personalised plan within one business day.
                  </h3>
                  <p className="text-sm text-char-mute leading-relaxed">
                    Want to talk sooner? Call <a href="tel:0494756111" className="text-ember">0494 756 111</a>.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <SisterServices keys={["menu", "food", "gbp", "brand"]} />
      <FAQSection
        eyebrow="Common questions · Restaurant marketing score"
        headline="Scoring, weights,"
        italic="and what to do next."
        intro="The scoring is intentionally simple — 8 honest questions get you to a roughly accurate band in 2 minutes."
        faqs={FAQS}
      />
      <FinalCTA
        eyebrow="Photography fix first · Highest ROI"
        headline="Most venues lift their score"
        italic="with photography first."
        intro="If your score is below 65 and the 'photography' or 'delivery' question scored no, that's almost always the highest-ROI fix to make first."
        backdropSlug="menu-malai-tikka"
      />
      <Footer />
    </div>
  );
}

const FAQS = [
  { q: "How is the score calculated?", a: "Each question has a weight based on its impact on growth — photography and delivery menu (the things customers see first) are weighted highest. A yes adds the question's weight to your score; no adds zero." },
  { q: "Is the scoring accurate?", a: "Within a band. Eight yes/no questions can't capture nuance, but they get you to roughly the right tier in 2 minutes. The personalised PDF follow-up gives you specifics." },
  { q: "Why does Grownomics get mentioned at the end?", a: "Because this scorecard was built with their data and benchmarks, and because most venues with a sub-65 score get the fastest results by combining a photography refresh (us) with paid + Google + email (them)." },
];
